import type { PaymentSetupAttempt } from '@billing/payment-sdk'
import type { Stripe, StripeCardCvcElement, StripeCardExpiryElement, StripeCardNumberElement, StripeElements } from '@stripe/stripe-js'
import * as Sentry from '@sentry/vue'
import { defineStore } from 'pinia'
import { usePaymentSetup } from '~/composables/api/usePaymentSetup'
import { translate } from '~/composables/translate/translations'
import { useExtrasStore } from '~/stores/extras'
import { useLocationStore } from '~/stores/location'
import { useOfferStore } from '~/stores/offer'
import { usePaymentProfileStore } from '~/stores/payment-profile'

/**
 * Pinia store for the payment setup flow in the pre-booking onboarding.
 *
 * Follows the project pattern: the page (payment.vue) writes Stripe instance
 * and attempt state into this store; SummaryHeader.vue reads from it and
 * calls the exposed actions to drive the CTA sequence.
 */

interface PaymentSetupState {
  /** Active setup attempt created on page load */
  setupAttempt?: PaymentSetupAttempt
  /** Stripe.js instance — set after initStripe() succeeds */
  stripe?: Stripe
  /** Stripe Elements factory — set after createElements() succeeds */
  elements?: StripeElements
  /** Individual Stripe card elements — set after mountCardElements() */
  cardNumber?: StripeCardNumberElement
  cardExpiry?: StripeCardExpiryElement
  cardCvc?: StripeCardCvcElement
  /** Cardholder name — collected via CtInput (outside Stripe iframe) */
  cardholderName: string
  /** Fiscal code value (Italy only) */
  fiscalCode: string
  /** Fiscal code input error message — set when updateBillingAddress fails */
  fiscalCodeError?: string
  /** Whether all three card elements are ready to submit */
  isStripeReady: boolean
  /** Whether the CTA sequence is in progress */
  isSubmitting: boolean
  /** Error message to display to the customer */
  errorMessage?: string
  /** Internal flag — set to true by reset() to cancel any in-flight poll */
  _pollCancelled: boolean
}

export const usePaymentSetupStore = defineStore('payment-setup', {
  state: (): PaymentSetupState => ({
    setupAttempt: undefined,
    stripe: undefined,
    elements: undefined,
    cardNumber: undefined,
    cardExpiry: undefined,
    cardCvc: undefined,
    cardholderName: '',
    fiscalCode: '',
    fiscalCodeError: undefined,
    isStripeReady: false,
    isSubmitting: false,
    errorMessage: undefined,
    _pollCancelled: false,
  }),

  actions: {
    reset() {
      this._pollCancelled = true
      this.setupAttempt = undefined
      this.stripe = undefined
      this.elements = undefined
      this.cardNumber = undefined
      this.cardExpiry = undefined
      this.cardCvc = undefined
      this.cardholderName = ''
      this.fiscalCode = ''
      this.fiscalCodeError = undefined
      this.errorMessage = undefined
      this.isStripeReady = false
      this.isSubmitting = false
    },

    /**
     * Runs the full payment setup sequence:
     *   1. (Italy only) updateFiscalCode — persist fiscal code before Stripe validation
     *   2. confirmCardSetup   — confirm the SetupIntent with Stripe (handles SCA)
     *   3. pollSetupAttempt   — wait for backend status SUCCEEDED
     *   4. createPreBookingAuthorization — pre-authorize the real offer amount
     *
     * Sets isSubmitting for the duration and errorMessage on failure.
     * Sets fiscalCodeError if the fiscal code update fails.
     * Returns true on success, false on any failure.
     */
    async performPrepaymentFlow(): Promise<string | undefined> {
      // Re-entrancy guard: prevent concurrent calls which could create duplicate holds
      if (this.isSubmitting)
        return undefined

      if (!this.stripe || !this.cardNumber || !this.setupAttempt)
        return undefined

      this.errorMessage = undefined

      const providerDetails = this.setupAttempt.providerDetails
      const clientSecret = providerDetails && 'stripeClientSecret' in providerDetails
        ? (providerDetails as { stripeClientSecret: string }).stripeClientSecret
        : undefined

      const paymentProfileStore = usePaymentProfileStore()

      if (!clientSecret || !paymentProfileStore.paymentProfileOwner)
        return undefined

      const offer = useOfferStore().selectedOffer
      if (!offer)
        return undefined

      this.isSubmitting = true
      this.errorMessage = undefined
      this.fiscalCodeError = undefined
      this._pollCancelled = false

      const { confirmCardSetup, pollSetupAttempt, createPreBookingAuthorization } = usePaymentSetup()

      try {
        // Step 1: Persist fiscal code for Italian customers before Stripe validation
        if (paymentProfileStore.isItalianCustomer && this.fiscalCode) {
          const fiscalCodeUpdated = await paymentProfileStore.updateFiscalCode(this.fiscalCode)
          if (!fiscalCodeUpdated) {
            this.fiscalCodeError = translate('payment_fiscal_code_error')
            return undefined
          }
        }

        // Step 2: Confirm card setup with Stripe (SCA handled automatically)
        const confirmError = await confirmCardSetup(
          this.stripe,
          clientSecret,
          this.cardNumber,
          this.cardholderName,
          paymentProfileStore.countryCode,
        )
        if (confirmError) {
          this.errorMessage = confirmError
          return undefined
        }

        // Step 3: Poll until backend setup attempt reaches SUCCEEDED
        try {
          await pollSetupAttempt(this.setupAttempt.id, () => this._pollCancelled)
        }
        catch (err: unknown) {
          this.errorMessage = err instanceof Error ? err.message : translate('payment_setup_failed')
          return undefined
        }

        // Bail out silently if reset() was called (component unmounted) during the poll
        if (this._pollCancelled)
          return undefined

        // Step 4: Pre-authorize the real offer amount
        const extraPrice = useExtrasStore().totalExtraPrice
        const totalPrice = (offer.price ?? 0) + extraPrice
        const result = await createPreBookingAuthorization(
          totalPrice,
          offer,
          paymentProfileStore.paymentProfileOwner,
          useLocationStore().city.legalEntityId,
        )
        if (result.error) {
          this.errorMessage = result.error.message
          Sentry.captureMessage(`Payment authorization returned error: ${result.error.message}`, {
            level: 'error',
            tags: { flow: 'new-user', step: 'payment-authorization' },
            extra: { errorCode: (result.error as any).code },
          })
          return undefined
        }

        if (result.paymentAuthorization?.status !== 'AUTHORIZED') {
          this.errorMessage = translate('payment_failed')
          Sentry.captureMessage(`Payment authorization not AUTHORIZED: status is ${result.paymentAuthorization?.status}`, {
            level: 'error',
            tags: { flow: 'new-user', step: 'payment-authorization' },
            extra: { status: result.paymentAuthorization?.status },
          })
          return undefined
        }

        return result.paymentAuthorization.uuid
      }
      finally {
        this.isSubmitting = false
        paymentProfileStore.loadPaymentProfileOwner()
      }
    },
  },
})
